function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }

function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }

function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }

function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }

import { createBoundedBuffer, display, canUseEventBridge, displayAlreadyInitializedError, willSyntheticsInjectRum, noop, timeStampNow, clocksNow, getEventBridge, initFeatureFlags, addTelemetryConfiguration, initFetchObservable, CustomerContextKey, buildAccountContextManager, buildGlobalContextManager, buildUserContextManager, monitorError, sanitize } from '@datadog/browser-core';
import { validateAndBuildRumConfiguration, fetchAndApplyRemoteConfiguration, serializeRumConfiguration } from '../domain/configuration';
import { startDurationVital as _startDurationVital, stopDurationVital as _stopDurationVital } from '../domain/vital/vitalCollection';
import { callPluginsMethod } from '../domain/plugins';
export function createPreStartStrategy(_ref, trackingConsentState, customVitalsState, doStartRum) {
  var _ref$ignoreInitIfSynt = _ref.ignoreInitIfSyntheticsWillInjectRum,
      ignoreInitIfSyntheticsWillInjectRum = _ref$ignoreInitIfSynt === void 0 ? true : _ref$ignoreInitIfSynt,
      startDeflateWorker = _ref.startDeflateWorker;
  var bufferApiCalls = createBoundedBuffer(); // TODO next major: remove the globalContextManager, userContextManager and accountContextManager from preStartStrategy and use an empty context instead

  var globalContext = buildGlobalContextManager();
  bufferContextCalls(globalContext, CustomerContextKey.globalContext, bufferApiCalls);
  var userContext = buildUserContextManager();
  bufferContextCalls(userContext, CustomerContextKey.userContext, bufferApiCalls);
  var accountContext = buildAccountContextManager();
  bufferContextCalls(accountContext, CustomerContextKey.accountContext, bufferApiCalls);
  var firstStartViewCall;
  var deflateWorker;
  var cachedInitConfiguration;
  var cachedConfiguration;
  var trackingConsentStateSubscription = trackingConsentState.observable.subscribe(tryStartRum);
  var emptyContext = {};

  function tryStartRum() {
    if (!cachedInitConfiguration || !cachedConfiguration || !trackingConsentState.isGranted()) {
      return;
    }

    trackingConsentStateSubscription.unsubscribe();
    var initialViewOptions;

    if (cachedConfiguration.trackViewsManually) {
      if (!firstStartViewCall) {
        return;
      } // An initial view is always created when starting RUM.
      // When tracking views automatically, any startView call before RUM start creates an extra
      // view.
      // When tracking views manually, we use the ViewOptions from the first startView call as the
      // initial view options, and we remove the actual startView call so we don't create an extra
      // view.


      bufferApiCalls.remove(firstStartViewCall.callback);
      initialViewOptions = firstStartViewCall.options;
    }

    var startRumResult = doStartRum(cachedConfiguration, deflateWorker, initialViewOptions);
    bufferApiCalls.drain(startRumResult);
  }

  function doInit(initConfiguration, errorStack) {
    var eventBridgeAvailable = canUseEventBridge();

    if (eventBridgeAvailable) {
      initConfiguration = overrideInitConfigurationForBridge(initConfiguration);
    } // Update the exposed initConfiguration to reflect the bridge and remote configuration overrides


    cachedInitConfiguration = initConfiguration;
    addTelemetryConfiguration(serializeRumConfiguration(initConfiguration));

    if (cachedConfiguration) {
      displayAlreadyInitializedError('DD_RUM', initConfiguration);
      return;
    }

    var configuration = validateAndBuildRumConfiguration(initConfiguration, errorStack);

    if (!configuration) {
      return;
    }

    if (!eventBridgeAvailable && !configuration.sessionStoreStrategyType) {
      display.warn('No storage available for session. We will not send any data.');
      return;
    }

    if (configuration.compressIntakeRequests && !eventBridgeAvailable && startDeflateWorker) {
      deflateWorker = startDeflateWorker(configuration, 'Datadog RUM', // Worker initialization can fail asynchronously, especially in Firefox where even CSP
      // issues are reported asynchronously. For now, the SDK will continue its execution even if
      // data won't be sent to Datadog. We could improve this behavior in the future.
      noop);

      if (!deflateWorker) {
        // `startDeflateWorker` should have logged an error message explaining the issue
        return;
      }
    }

    cachedConfiguration = configuration; // Instrument fetch to track network requests
    // This is needed in case the consent is not granted and some customer
    // library (Apollo Client) is storing uninstrumented fetch to be used later
    // The subscription is needed so that the instrumentation process is completed

    initFetchObservable().subscribe(noop);
    trackingConsentState.tryToInit(configuration.trackingConsent);
    tryStartRum();
  }

  var addDurationVital = function addDurationVital(vital) {
    bufferApiCalls.add(function (startRumResult) {
      return startRumResult.addDurationVital(vital);
    });
  };

  var addOperationStepVital = function addOperationStepVital(name, stepType, options, failureReason) {
    bufferApiCalls.add(function (startRumResult) {
      return startRumResult.addOperationStepVital(sanitize(name), stepType, sanitize(options), sanitize(failureReason));
    });
  };

  var strategy = {
    init: function init(initConfiguration, publicApi, errorStack) {
      if (!initConfiguration) {
        display.error('Missing configuration');
        return;
      } // Set the experimental feature flags as early as possible, so we can use them in most places


      initFeatureFlags(initConfiguration.enableExperimentalFeatures); // Expose the initial configuration regardless of initialization success.

      cachedInitConfiguration = initConfiguration; // If we are in a Synthetics test configured to automatically inject a RUM instance, we want
      // to completely discard the customer application RUM instance by ignoring their init() call.
      // But, we should not ignore the init() call from the Synthetics-injected RUM instance, so the
      // internal `ignoreInitIfSyntheticsWillInjectRum` option is here to bypass this condition.

      if (ignoreInitIfSyntheticsWillInjectRum && willSyntheticsInjectRum()) {
        return;
      }

      callPluginsMethod(initConfiguration.plugins, 'onInit', {
        initConfiguration: initConfiguration,
        publicApi: publicApi
      });

      if (initConfiguration.remoteConfigurationId) {
        fetchAndApplyRemoteConfiguration(initConfiguration, {
          user: userContext,
          context: globalContext
        }).then(function (initConfiguration) {
          if (initConfiguration) {
            doInit(initConfiguration, errorStack);
          }
        })["catch"](monitorError);
      } else {
        doInit(initConfiguration, errorStack);
      }
    },

    get initConfiguration() {
      return cachedInitConfiguration;
    },

    getInternalContext: noop,
    stopSession: noop,
    addTiming: function addTiming(name) {
      var time = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : timeStampNow();
      bufferApiCalls.add(function (startRumResult) {
        return startRumResult.addTiming(name, time);
      });
    },
    startView: function startView(options) {
      var startClocks = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : clocksNow();

      var callback = function callback(startRumResult) {
        startRumResult.startView(options, startClocks);
      };

      bufferApiCalls.add(callback);

      if (!firstStartViewCall) {
        firstStartViewCall = {
          options: options,
          callback: callback
        };
        tryStartRum();
      }
    },
    setViewName: function setViewName(name) {
      bufferApiCalls.add(function (startRumResult) {
        return startRumResult.setViewName(name);
      });
    },
    // View context APIs
    setViewContext: function setViewContext(context) {
      bufferApiCalls.add(function (startRumResult) {
        return startRumResult.setViewContext(context);
      });
    },
    setViewContextProperty: function setViewContextProperty(key, value) {
      bufferApiCalls.add(function (startRumResult) {
        return startRumResult.setViewContextProperty(key, value);
      });
    },
    getViewContext: function getViewContext() {
      return emptyContext;
    },
    globalContext: globalContext,
    userContext: userContext,
    accountContext: accountContext,
    addAction: function addAction(action) {
      bufferApiCalls.add(function (startRumResult) {
        return startRumResult.addAction(action);
      });
    },
    addError: function addError(providedError) {
      bufferApiCalls.add(function (startRumResult) {
        return startRumResult.addError(providedError);
      });
    },
    addFeatureFlagEvaluation: function addFeatureFlagEvaluation(key, value) {
      bufferApiCalls.add(function (startRumResult) {
        return startRumResult.addFeatureFlagEvaluation(key, value);
      });
    },
    startDurationVital: function startDurationVital(name, options) {
      return _startDurationVital(customVitalsState, name, options);
    },
    stopDurationVital: function stopDurationVital(name, options) {
      _stopDurationVital(addDurationVital, customVitalsState, name, options);
    },
    addDurationVital: addDurationVital,
    addOperationStepVital: addOperationStepVital
  };
  return strategy;
}

function overrideInitConfigurationForBridge(initConfiguration) {
  var _a, _b;

  return _objectSpread(_objectSpread({}, initConfiguration), {}, {
    applicationId: '00000000-aaaa-0000-aaaa-000000000000',
    clientToken: 'empty',
    sessionSampleRate: 100,
    defaultPrivacyLevel: (_a = initConfiguration.defaultPrivacyLevel) !== null && _a !== void 0 ? _a : (_b = getEventBridge()) === null || _b === void 0 ? void 0 : _b.getPrivacyLevel()
  });
}

function bufferContextCalls(preStartContextManager, name, bufferApiCalls) {
  preStartContextManager.changeObservable.subscribe(function () {
    var context = preStartContextManager.getContext();
    bufferApiCalls.add(function (startRumResult) {
      return startRumResult[name].setContext(context);
    });
  });
}